import { AttachmentsAdditionalProperties, withDisplayName } from "@components/smart/GeneralFieldDefinition";
import {
    FilterBarGroup,
    getDefaultFilterGroupDef,
    IFilterGroupDef
} from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { DocumentDeleteLogEntity, EntitySetName, EntityTypeName } from "@odata/GeneratedEntityTypes";
import { DocumentTypeCode } from "@odata/GeneratedEnums";
import { IFormatOptions } from "@odata/OData.utils";
import { userNameWithAvatarFormatter } from "@pages/admin/users/Users.utils";
import i18next from "i18next";

import { Sort, TextAlign } from "../../enums";
import { TValue } from "../../global.types";
import BindingContext from "../../odata/BindingContext";
import DateType from "../../types/Date";
import { ISplitPageTableDef } from "../../views/table/TableView.utils";
import { setDefByEntityType } from "../getDefByEntityType";
import { IDefinition, IGetDefinition } from "../PageUtils";

export const getDefinitions: IGetDefinition = (): IDefinition => {
    const filterBarDef: IFilterGroupDef[] = [{
        ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
        allowCustomFilters: false,
        createQuery: false,
        isValueHelp: true,
        defaultFilters: [DocumentDeleteLogEntity.NumberOurs, DocumentDeleteLogEntity.DocumentType, DocumentDeleteLogEntity.DateCreated,
            DocumentDeleteLogEntity.InteractionType],
        filterDefinition: {
            [DocumentDeleteLogEntity.NumberOurs]: {},
            [DocumentDeleteLogEntity.DateCreated]: {
                label: i18next.t("Document:DeleteLog.DeleteTime")
            },
            ...withDisplayName(DocumentDeleteLogEntity.DocumentType),
            ...withDisplayName(DocumentDeleteLogEntity.InteractionType),
            ...withDisplayName(DocumentDeleteLogEntity.LastModifiedBy, {
                label: i18next.t("Document:DeleteLog.User")
            })
        }
    }];

    const table: ISplitPageTableDef = {
        id: `${EntityTypeName.DocumentDeleteLog}Table`,
        filterBarDef,
        // todo: check if attachments needs to be expanded in table request or could be postponed on form load.
        additionalProperties: [{ id: "DocumentId" }, ...AttachmentsAdditionalProperties],
        columns: [DocumentDeleteLogEntity.DocumentType, DocumentDeleteLogEntity.NumberOurs, DocumentDeleteLogEntity.DateCreated,
            BindingContext.localContext("HoursCreated"),
            DocumentDeleteLogEntity.InteractionType, DocumentDeleteLogEntity.LastModifiedBy],
        columnDefinition: {
            [DocumentDeleteLogEntity.NumberOurs]: {},
            [DocumentDeleteLogEntity.DocumentType]: {
                fieldSettings: {
                    displayName: "Name"
                },
                formatter: (val: TValue, args: IFormatOptions): string => {
                    const type = args.item.DocumentType?.Code;
                    if ([DocumentTypeCode.ProformaInvoiceReceived, DocumentTypeCode.ProformaInvoiceIssued].includes(type)) {
                        return i18next.t(`Common:DocumentTypeName.${type === DocumentTypeCode.ProformaInvoiceReceived ? "Received" : "Issued"}DDOPP`).toString();
                    }

                    return val as string;
                }
            },
            [DocumentDeleteLogEntity.DateCreated]: {
                label: i18next.t("Document:DeleteLog.DeleteTime")
            },
            [BindingContext.localContext("HoursCreated")]: {
                label: i18next.t("Common:General.DeleteTime"),
                textAlign: TextAlign.Right,
                fieldSettings: {
                    disableSort: true
                },
                formatter: (val: TValue, args: IFormatOptions) => {
                    const date = args.item[DocumentDeleteLogEntity.DateCreated];
                    return DateType.localFormat(date, DateType.defaultTimeFormat);
                }
            },
            ...withDisplayName(DocumentDeleteLogEntity.LastModifiedBy, {
                label: i18next.t("Document:DeleteLog.User"),
                formatter: (val: TValue) => {
                    const fullName = val.toString()?.split(" ");
                    const user = {
                        FirstName: fullName?.[0] ?? "",
                        LastName: fullName?.[1] ?? ""
                    };
                    return userNameWithAvatarFormatter(user.FirstName, { entity: user }, {
                        isBold: true,
                        withLink: false
                    });
                }
            }),
            ...withDisplayName(DocumentDeleteLogEntity.InteractionType)
        },
        title: i18next.t("Document:DeleteLog.Title"),
        initialSortBy: [{ id: DocumentDeleteLogEntity.DateCreated, sort: Sort.Desc }]
    };


    return {
        entitySet: EntitySetName.DocumentDeleteLogs,
        table
    };
};

getDefinitions.translationFiles = ["Common", "Document"];
setDefByEntityType(EntityTypeName.DocumentDeleteLog, getDefinitions);
